<template>
	<StoryblokComponent v-if="story" :blok="story.content" />
</template>

<script setup lang="ts">
const { slug } = useRoute().params;
const { t } = useI18n();
const storyblokLanguage = useStoryblokLanguage();
const { setPageType } = usePageStore();
const config = useRuntimeConfig();
const {
	public: {
		categoryContentKey,
		storyblokVersion,
		norce: { isHelloRetailTracking }
	}
} = config;
const { helloRetailApi } = useNorcePwa();
const storyblokApi = useStoryblokApi();

const isCategoryContent = Array.isArray(slug)
	? slug.includes(categoryContentKey)
	: slug === categoryContentKey;

if (isCategoryContent) {
	useSeoMeta({
		robots: "noindex, nofollow"
	});
}

const path = Array.isArray(slug) && slug.length > 0 ? slug.join("/") : "home";
const story = ref();
// Live-sync with the Storyblok Visual Editor (no-op outside the editor).
// Registered before the await so the onMounted hook is set up while the
// component instance is still active.
useStoryblokBridgeStory(story, { resolveLinks: "url" });

// useAsyncData ensures the fetched story is included in the Nuxt SSR
// payload so the client hydrates from the same data the server rendered,
// rather than independently re-fetching. Without this, a CDN stale-hit
// (old HTML) combined with a fresh client fetch (new Storyblok content)
// produces a hydration mismatch that causes blocks to visually collapse.
const { data: fetchedStory } = await useAsyncData(
	`cms-story-${path}-${storyblokLanguage.value}`,
	async () => {
		try {
			const sbVersion = storyblokVersion as unknown as "published" | "draft";
			const { data } = await storyblokApi.get(`cdn/stories/${path}`, {
				version: sbVersion,
				language: storyblokLanguage.value,
				resolve_links: "url"
			});
			return data.story ?? null;
		} catch {
			return null;
		}
	}
);

if (!fetchedStory.value) {
	showError({
		statusCode: 404,
		statusMessage: t("errors.page_not_found.title"),
		message: t("errors.page_not_found.message")
	});
}

story.value = fetchedStory.value;

if (story.value) {
	if (!slug?.length) {
		setPageType("home");
	} else {
		setPageType("cms");
	}
}
useCmsSeoMeta(story.value);

useCmsSchemaOrg(story.value);

onMounted(() => {
	if (!isHelloRetailTracking) return;

	helloRetailApi.track({
		serviceType: "pageview",
		trackingData: {
			location: window.location.href,
			referrer: window.location.origin,
			url: window.location.href
		}
	});
});
</script>
